import React, { useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, useScroll, useTransform } from 'framer-motion';
import { FaReact, FaNodeJs, FaPython, FaJava, FaPhp, FaAngular, FaVuejs } from 'react-icons/fa';
import { SiFlutter, SiJavascript, SiMongodb } from 'react-icons/si';
import { Canvas, useThree } from '@react-three/fiber';
import { Float, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

/* ============================
   3D section elements (unchanged logic)
   ============================ */
const TechSection3DElements = () => {
  const { scene } = useThree();

  useEffect(() => {
    scene.fog = new THREE.Fog('#0f0f0f', 1, 20);
  }, [scene]);

  return (
    <>
      <ambientLight intensity={0.3} />
      <directionalLight position={[10, 10, 5]} intensity={1} />
      <pointLight position={[-10, -10, -5]} intensity={0.5} color="#4a90e2" />
      <pointLight position={[10, -10, -5]} intensity={0.5} color="#FF6B35" />
      <pointLight position={[0, 10, -5]} intensity={0.3} color="#8b5cf6" />

      <Float speed={1.6} rotationIntensity={0.5} floatIntensity={0.5}>
        <mesh position={[-4, 2, 0]}>
          <sphereGeometry args={[0.8, 32, 32]} />
          <meshStandardMaterial color="#4a90e2" transparent opacity={0.6} emissive="#4a90e2" emissiveIntensity={0.2} />
        </mesh>
      </Float>

      <Float speed={2.0} rotationIntensity={0.4} floatIntensity={0.4}>
        <mesh position={[4, -1, 2]}>
          <boxGeometry args={[1.2, 1.2, 1.2]} />
          <meshStandardMaterial color="#FF6B35" transparent opacity={0.6} emissive="#FF6B35" emissiveIntensity={0.2} />
        </mesh>
      </Float>

      <Float speed={1.4} rotationIntensity={0.6} floatIntensity={0.6}>
        <mesh position={[0, 3, -2]}>
          <torusGeometry args={[0.9, 0.3, 16, 100]} />
          <meshStandardMaterial color="#8b5cf6" transparent opacity={0.6} emissive="#8b5cf6" emissiveIntensity={0.2} />
        </mesh>
      </Float>

      <Float speed={2.3} rotationIntensity={0.7} floatIntensity={0.7}>
        <mesh position={[-2, -2, 1]}>
          <cylinderGeometry args={[0.7, 0.7, 1.3, 32]} />
          <meshStandardMaterial color="#fbbf24" transparent opacity={0.6} emissive="#fbbf24" emissiveIntensity={0.2} />
        </mesh>
      </Float>

      <Float speed={1.8} rotationIntensity={0.5} floatIntensity={0.5}>
        <mesh position={[2, 1, -1]}>
          <octahedronGeometry args={[0.7]} />
          <meshStandardMaterial color="#22d3ee" transparent opacity={0.6} emissive="#22d3ee" emissiveIntensity={0.2} />
        </mesh>
      </Float>
    </>
  );
};

/* ============================
   Styled components (responsive)
   ============================ */

const TechSectionContainer = styled.section`
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 50%, #0f0f0f 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 15px,

  /* Slightly smaller min-height on very small screens so content doesn't feel too tall */
  @media (max-height: 640px) {
    min-height: 80vh;
  }
`;

const CanvasContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  /* Keep canvas full, but on small screens ensure pointer interactions don't fight scroll */
  @media (max-width: 768px) {
    /* not disabling pointer events by default — leave interactions intact.
       If you want touch to ignore canvas to prioritize scrolling, set pointer-events: none; */
  }
`;

const ContentOverlay = styled.div`
  position: relative;
  z-index: 10;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;

  @media (max-width: 1024px) {
    padding: 48px 16px;
  }
  @media (max-width: 768px) {
    padding: 36px 14px;
    align-items: flex-start;
  }
  @media (max-width: 480px) {
    padding: 28px 12px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
`;

/* Title - uses clamp so it scales smoothly */
const SectionTitle = styled.h2`
  font-size: clamp(1.8rem, 4.5vw, 3rem);
  color: #ffffff;
  margin-bottom: 40px;
  text-align: center;
  font-family: 'Manrope', sans-serif;
  font-weight: 700;
  background: linear-gradient(45deg, #FF6B35, #4a90e2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 0 24px rgba(74, 144, 226, 0.25);

  @media (max-width: 480px) {
    margin-bottom: 28px;
  }
`;

/* Icon scroller animation */
const moveIcons = keyframes`
  0% { transform: translateX(-40%); }
  100% { transform: translateX(0%); }
`;

const IconScrollerContainer = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto 48px auto;
  overflow: hidden;
  background: transparent;
  padding: 12px 0;
  display: flex;
  align-items: center;
  position: relative;

  /* shorter row and faster animation on smaller screens */
  --scroll-duration: 20s;
  @media (max-width: 1200px) { --scroll-duration: 16s; max-width: 720px; }
  @media (max-width: 768px)  { --scroll-duration: 12s; max-width: 520px; margin-bottom: 32px; }
  @media (max-width: 480px)  { --scroll-duration: 10s; max-width: 420px; margin-bottom: 20px; }
`;

/* Icon row uses the CSS variable for animation speed, and reduced gaps on small screens */
const IconRow = styled.div`
  display: flex;
  align-items: center;
  gap: 48px;
  animation: ${moveIcons} var(--scroll-duration) linear infinite;
  will-change: transform;
  white-space: nowrap;

  @media (max-width: 1200px) { gap: 36px; }
  @media (max-width: 768px)  { gap: 28px; }
  @media (max-width: 480px)  { gap: 20px; }
`;

/* Scroller icons (bigger, circular) */
const ScrollerIcon = styled(motion.div)`
  font-size: clamp(1.2rem, 2.2vw, 1.8rem);
  color: #4a90e2;
  background: rgba(0, 0, 0, 0.75);
  border-radius: 50%;
  width: clamp(48px, 6.5vw, 60px);
  height: clamp(48px, 6.5vw, 60px);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(74, 144, 226, 0.18);
  border: 2px solid rgba(74, 144, 226, 0.28);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
`;

/* Tech grid: responsive columns */
const TechGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  @media (max-width: 480px) {
    gap: 14px;
  }
`;

/* Card inside grid */
const TechCard = styled(motion.div)`
  background: rgba(26, 26, 26, 0.9);
  backdrop-filter: blur(12px);
  border-radius: 14px;
  padding: 22px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  transition: transform 0.35s cubic-bezier(.17,.67,.2,1), box-shadow 0.35s;

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 40px rgba(74,144,226,0.15);
  }

  @media (max-width: 768px) {
    padding: 18px;
  }

  @media (max-width: 480px) {
    padding: 14px;
  }
`;

/* Small icon inside card (different size than scroller icons) */
const CardIcon = styled.div`
  font-size: clamp(1rem, 2.2vw, 1.4rem);
  width: clamp(40px, 6vw, 48px);
  height: clamp(40px, 6vw, 48px);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.6);
  color: #fff;
  flex-shrink: 0;
`;

/* Card content text */
const TechContent = styled.div`
  flex: 1;
`;

const TechTitle = styled.h3`
  font-size: clamp(0.95rem, 1.7vw, 1.05rem);
  color: #ffffff;
  margin: 0;
  font-family: 'Roboto', sans-serif;
  font-weight: 600;
  line-height: 1.25;
`;

/* ============================
   Component (logic preserved)
   ============================ */

const TechSection = () => {
  const containerRef = useRef();
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  const titleY = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const gridY = useTransform(scrollYProgress, [0, 1], [200, -200]);

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        delay: i * 0.08,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    }),
  };

  const techIcons = [
    <FaReact key="react" />, <SiFlutter key="flutter" />, <FaNodeJs key="node" />,
    <FaPython key="python" />, <FaJava key="java" />, <FaPhp key="php" />,
    <FaAngular key="angular" />, <FaVuejs key="vue" />, <SiJavascript key="js" />,
    <SiMongodb key="mongo" />
  ];

  const technologies = [
    { icon: '⚛️', title: 'React.js' },
    { icon: '🔷', title: 'Node.js' },
    { icon: '🐍', title: 'Python' },
    { icon: '☕', title: 'Java' },
    { icon: '💎', title: 'Ruby' },
    { icon: '🐘', title: 'PHP' },
    { icon: '⚡', title: 'Vue.js' },
    { icon: '🅰️', title: 'Angular' },
  ];

  return (
    <TechSectionContainer ref={containerRef}>
      <CanvasContainer>
        <Canvas camera={{ position: [0, 0, 8], fov: 75 }}>
          <TechSection3DElements />
          <OrbitControls
            enableZoom={false}
            enablePan={false}
            autoRotate
            autoRotateSpeed={0.3}
            maxPolarAngle={Math.PI / 2}
            minPolarAngle={Math.PI / 2}
          />
        </Canvas>
      </CanvasContainer>

      <ContentOverlay>
        <ContentWrapper>
          {/* Animated Icon Row */}
          <IconScrollerContainer>
            <IconRow>
              {techIcons.concat(techIcons).map((icon, idx) => (
                <ScrollerIcon key={idx} whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }}>
                  {icon}
                </ScrollerIcon>
              ))}
            </IconRow>
          </IconScrollerContainer>

          <SectionTitle
          // style={{ y: titleY }}
          // initial={{ opacity: 0, y: 50 }}
          // whileInView={{ opacity: 1, y: 0 }}
          // transition={{ duration: 0.9 }}
          >
            Technology We Used
          </SectionTitle>

          <TechGrid style={{ y: gridY }} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            {technologies.map((tech, index) => (
              <TechCard key={index} custom={index} variants={cardVariants} whileHover={{ y: -6 }} whileTap={{ scale: 0.97 }}>
                <CardIcon>{tech.icon}</CardIcon>
                <TechContent>
                  <TechTitle>{tech.title}</TechTitle>
                </TechContent>
              </TechCard>
            ))}
          </TechGrid>
        </ContentWrapper>
      </ContentOverlay>
    </TechSectionContainer>
  );
};

export default TechSection;
